import { type RequestOptions, clientPanich } from "../client.panich";
import { type operations } from "../client.panich/schema";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// /get-user
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

type GetUserInfoOperation = operations["get_user_auth_get_user_get"];
export type GetUserInfoProps = never;
export type GetUserInfoReturn = GetUserInfoOperation["responses"]["200"]["content"]["application/json"];

export const getUserInfo = async (): Promise<GetUserInfoReturn> => {
  // Will return null if user is un authorized
  const { data } = await clientPanich.GET(
    "/auth/get-user",
    {
      credentials: "include",
      cache: "no-store",
    },
  );
  return data;
};

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// /login
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

type LoginOperation = operations["login_auth_login_post"];
export type LoginProps = LoginOperation["parameters"]["query"] & LoginOperation["requestBody"]["content"]["application/json"];
export type LoginReturn = LoginOperation["responses"]["200"]["content"]["application/json"];

export const login = async (
  props: LoginProps,
  requestOptions?: RequestOptions,
): Promise<LoginReturn> => {
  const { data } = await clientPanich.POST(
    "/auth/login",
    {
      body: {
        usr: props.usr,
        pwd: props.pwd,
      },
      params: {
        query: {
          forAccountMerge: props.forAccountMerge,
        },
      },
      credentials: "include",
      cache: "no-store",
      ...requestOptions,
    },
  );
  return data;
};

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// /logout
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

type LogoutOperation = operations["logout_auth_logout_post"];
export type LogoutProps = never;
export type LogoutReturn = LogoutOperation["responses"]["200"]["content"]["application/json"];

export const logout = async (): Promise<LogoutReturn> => {
  const { data } = await clientPanich.POST(
    "/auth/logout",
    {
      credentials: "include",
      cache: "no-store",
    },
  );
  return data;
};

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// /reset-password
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

type ResetPasswordOperation = operations["reset_password_auth_reset_password_post"];
export type ResetPasswordProps = Exclude<ResetPasswordOperation["requestBody"]["content"]["application/json"], undefined>;
export type ResetPasswordReturn = ResetPasswordOperation["responses"]["200"]["content"]["application/json"];

export const resetPassword = async (
  props: ResetPasswordProps,
  requestOptions?: RequestOptions,
): Promise<ResetPasswordReturn> => {
  const { data } = await clientPanich.POST(
    "/auth/reset-password",
    {
      body: props,
      credentials: "include",
      cache: "no-store",
      ...requestOptions,
    },
  );
  return data;
};

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// /signup
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

type SignupOperation = operations["signup_auth_signup_post"];
export type SignupProps = SignupOperation["parameters"]["query"] & SignupOperation["requestBody"]["content"]["application/json"];
export type SignupReturn = SignupOperation["responses"]["200"]["content"]["application/json"];

export const signup = async (
  props: SignupProps,
  requestOptions?: RequestOptions,
): Promise<SignupReturn> => {
  const { data } = await clientPanich.POST(
    "/auth/signup",
    {
      body: {
        usr: props.usr,
        pwd: props.pwd,
      },
      params: {
        query: {
          forAccountMerge: props.forAccountMerge,
        },
      },
      credentials: "include",
      cache: "no-store",
      ...requestOptions,
    },
  );
  return data;
};

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// /email-verification
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

type EmailVerificationOperation = operations["email_verification_auth_email_verification_post"];
export type EmailVerificationProps = Exclude<EmailVerificationOperation["requestBody"]["content"]["application/json"], undefined>;
export type EmailVerificationReturn = EmailVerificationOperation["responses"]["200"]["content"]["application/json"];

export const emailVerification = async (
  props: EmailVerificationProps,
  requestOptions?: RequestOptions,
): Promise<EmailVerificationReturn> => {
  const { data } = await clientPanich.POST(
    "/auth/email-verification",
    {
      body: props,
      credentials: "include",
      cache: "no-store",
      ...requestOptions,
    },
  );
  return data;
};

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// /check-signup-process
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

type CheckSignupProcessOperation = operations["check_signup_process_auth_check_signup_process_get"];
export type CheckSignupProcessProps = Exclude<CheckSignupProcessOperation["parameters"]["query"], undefined>;
export type CheckSignupProcessReturn = CheckSignupProcessOperation["responses"]["200"]["content"]["application/json"];

export const checkSignupProcess = async (
  props: CheckSignupProcessProps,
  requestOptions?: RequestOptions,
): Promise<CheckSignupProcessReturn> => {
  const { data } = await clientPanich.GET(
    "/auth/check-signup-process",
    {
      params: {
        query: props,
      },
      credentials: "include",
      cache: "no-store",
      ...requestOptions,
    },
  );
  return data;
};

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// /update-password
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

type UpdatePasswordOperation = operations["update_password_auth_update_password_post"];
export type UpdatePasswordProps = Exclude<UpdatePasswordOperation["requestBody"]["content"]["application/json"], undefined>;
export type UpdatePasswordReturn = UpdatePasswordOperation["responses"]["200"]["content"]["application/json"];

export const updatePassword = async (
  props: UpdatePasswordProps,
  requestOptions?: RequestOptions,
): Promise<UpdatePasswordReturn> => {
  const { data } = await clientPanich.POST(
    "/auth/update-password",
    {
      body: props,
      credentials: "include",

      ...requestOptions,
      cache: "no-store",
    },
  );
  return data;
};

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// /update-password-with-key
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

type UpdatePasswordWithKeyOperation = operations["update_password_with_key_auth_update_password_with_key_post"];
export type UpdatePasswordWithKeyProps = Exclude<UpdatePasswordWithKeyOperation["requestBody"]["content"]["application/json"], undefined>;
export type UpdatePasswordWithKeyReturn = UpdatePasswordWithKeyOperation["responses"]["200"]["content"]["application/json"];

export const updatePasswordWithKey = async (
  props: UpdatePasswordWithKeyProps,
  requestOptions?: RequestOptions,
): Promise<UpdatePasswordWithKeyReturn> => {
  const { data } = await clientPanich.POST(
    "/auth/update-password-with-key",
    {
      body: props,
      credentials: "include",
      cache: "no-store",
      ...requestOptions,
    },
  );
  return data;
};

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// /get-auth-url/{provider}
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

type GetAuthUrlOperation = operations["get_auth_url_auth_get_auth_url__provider__get"];
type GetAuthUrlProps = GetAuthUrlOperation["parameters"]["path"] & GetAuthUrlOperation["parameters"]["query"];
type GetAuthUrlReturn = GetAuthUrlOperation["responses"]["200"]["content"]["application/json"];

export const getAuthUrl = async (
  props: GetAuthUrlProps,
  requestOptions?: RequestOptions,
): Promise<GetAuthUrlReturn> => {
  const { data } = await clientPanich.GET(
    `/auth/get-auth-url/{provider}`,
    {
      params: {
        query: {
          redirectUrl: props.redirectUrl,
          forAccountMerge: props.forAccountMerge,
        },
        path: {
          provider: props.provider,
        },
      },
      credentials: "include",
      cache: "no-store",
      ...requestOptions,
    },
  );
  return data;
};
